/**
 * WebGL canvas relighting for the Gravity studio simulator.
 * Derives a normal map from the photo luminance and relights via ratio compositing
 * so painted canvas texture (albedo) stays pixel-faithful.
 */
(function (global) {
  'use strict';

  const MAX_TEX = 2048;

  const VERT_SRC =
    'attribute vec2 a_pos;' +
    'varying vec2 v_uv;' +
    'void main(){' +
    'v_uv=a_pos*0.5+0.5;' +
    'gl_Position=vec4(a_pos,0.0,1.0);' +
    '}';

  const FRAG_SRC =
    'precision highp float;' +
    'varying vec2 v_uv;' +
    'uniform sampler2D u_albedo;' +
    'uniform sampler2D u_normal;' +
    'uniform vec2 u_lightPos;' +
    'uniform float u_intensity;' +
    'uniform float u_ambient;' +
    'uniform float u_beam;' +
    'uniform float u_soft;' +
    'uniform vec3 u_lightColor;' +
    'uniform float u_viewAspect;' +
    'uniform vec2 u_texel;' +
    'uniform vec2 u_uvScale;' +
    'uniform vec2 u_uvOffset;' +
    'vec2 coverUv(vec2 screenUv){' +
    'return u_uvOffset+screenUv*u_uvScale;' +
    '}' +
    'float beamRadius(){' +
    'float t=clamp((u_beam-15.0)/165.0,0.0,1.0);' +
    'float base=mix(0.11,0.52,t);' +
    'return base*(mix(0.82,1.38,u_soft));' +
    '}' +
    'float spotMask(vec2 uv){' +
    'vec2 d=vec2((uv.x-u_lightPos.x)*u_viewAspect,uv.y-u_lightPos.y);' +
    'float dist=length(d);' +
    'float r=beamRadius();' +
    'float core=1.0-smoothstep(r*0.22,r*0.95,dist);' +
    'return pow(clamp(core,0.0,1.0),mix(2.4,1.15,u_soft));' +
    '}' +
    'void main(){' +
    'vec2 screenUv=vec2(v_uv.x,1.0-v_uv.y);' +
    'vec2 uv=coverUv(screenUv);' +
    'if(uv.x<0.0||uv.x>1.0||uv.y<0.0||uv.y>1.0){gl_FragColor=vec4(0.0,0.0,0.0,1.0);return;}' +
    'vec3 albedo=texture2D(u_albedo,uv).rgb;' +
    'vec3 N=normalize(texture2D(u_normal,uv).rgb*2.0-1.0);' +
    'vec3 L=normalize(vec3((u_lightPos.x-screenUv.x)*u_viewAspect,(u_lightPos.y-screenUv.y),mix(0.45,0.72,u_soft)));' +
    'float NdotL=max(dot(N,L),0.0);' +
    'float spot=spotMask(screenUv);' +
    'float I=u_intensity;' +
    'float A=u_ambient;' +
    'float studioBase=mix(0.06,0.88,A)+u_soft*0.05;' +
    'vec3 color=albedo*studioBase;' +
    'float falloffAmt=(1.0-spot)*(mix(0.38,0.14,A)+I*mix(0.38,0.22,u_soft));' +
    'color*=1.0-falloffAmt;' +
    'float beamLift=spot*(0.28+I*mix(0.55,0.34,u_soft));' +
    'beamLift*=0.55+0.45*NdotL;' +
    'color+=albedo*beamLift*mix(vec3(1.0),u_lightColor,0.22);' +
    'float tintAmt=spot*I*mix(0.14,0.08,u_soft);' +
    'color=mix(color,color*u_lightColor,tintAmt);' +
    'if(u_soft<0.5){' +
    'vec3 V=vec3(0.0,0.0,1.0);' +
    'vec3 H=normalize(L+V);' +
    'float spec=pow(max(dot(N,H),0.0),30.0)*spot*I*0.09;' +
    'color+=spec*u_lightColor;' +
    '}' +
    'float grain=dot(texture2D(u_albedo,uv+u_texel*vec2(1.0,0.0)).rgb,vec3(0.333))-' +
    'dot(texture2D(u_albedo,uv-u_texel*vec2(1.0,0.0)).rgb,vec3(0.333));' +
    'color+=grain*spot*NdotL*I*0.05;' +
    'gl_FragColor=vec4(clamp(color,0.0,1.0),1.0);' +
    '}';

  function createShader(gl, type, src) {
    const sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      const msg = gl.getShaderInfoLog(sh) || 'shader compile failed';
      gl.deleteShader(sh);
      throw new Error(msg);
    }
    return sh;
  }

  function createProgram(gl, vs, fs) {
    const prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      const msg = gl.getProgramInfoLog(prog) || 'program link failed';
      gl.deleteProgram(prog);
      throw new Error(msg);
    }
    return prog;
  }

  function boxBlur(src, w, h, radius) {
    const tmp = new Float32Array(src.length);
    const out = new Float32Array(src.length);
    const r = Math.max(1, radius);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let sum = 0;
        let n = 0;
        for (let kx = -r; kx <= r; kx++) {
          const cx = Math.min(w - 1, Math.max(0, x + kx));
          sum += src[y * w + cx];
          n++;
        }
        tmp[y * w + x] = sum / n;
      }
    }
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let sum = 0;
        let n = 0;
        for (let ky = -r; ky <= r; ky++) {
          const cy = Math.min(h - 1, Math.max(0, y + ky));
          sum += tmp[cy * w + x];
          n++;
        }
        out[y * w + x] = sum / n;
      }
    }
    return out;
  }

  function buildNormalMapFromImage(img) {
    const scale = Math.min(1, MAX_TEX / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(2, Math.round(img.naturalWidth * scale));
    const h = Math.max(2, Math.round(img.naturalHeight * scale));
    const scratch = document.createElement('canvas');
    scratch.width = w;
    scratch.height = h;
    const ctx = scratch.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);
    const lum = new Float32Array(w * h);
    for (let i = 0; i < w * h; i++) {
      const o = i * 4;
      lum[i] = (data[o] * 0.299 + data[o + 1] * 0.587 + data[o + 2] * 0.114) / 255;
    }
    const low = boxBlur(lum, w, h, Math.max(2, Math.round(Math.min(w, h) * 0.012)));
    const detail = new Float32Array(w * h);
    for (let i = 0; i < lum.length; i++) {
      detail[i] = lum[i] - low[i];
    }
    const normals = new Uint8Array(w * h * 4);
    const strength = 7.5;
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        let dx = 0;
        let dy = 0;
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const k = (y + ky) * w + (x + kx);
            const sx = kx === 0 ? 0 : kx;
            const sy = ky === 0 ? 0 : ky;
            if (kx === 0 && ky === 0) continue;
            const wgt = 1 / (Math.abs(kx) + Math.abs(ky));
            dx += detail[k] * sx * wgt;
            dy += detail[k] * sy * wgt;
          }
        }
        dx += (lum[(y) * w + (x + 1)] - lum[(y) * w + (x - 1)]) * 0.35;
        dy += (lum[(y + 1) * w + x] - lum[(y - 1) * w + x]) * 0.35;
        let nx = -dx * strength;
        let ny = -dy * strength;
        const nz = 1;
        const len = Math.hypot(nx, ny, nz) || 1;
        nx /= len;
        ny /= len;
        const nzN = nz / len;
        const idx = (y * w + x) * 4;
        normals[idx] = Math.round((nx * 0.5 + 0.5) * 255);
        normals[idx + 1] = Math.round((ny * 0.5 + 0.5) * 255);
        normals[idx + 2] = Math.round((nzN * 0.5 + 0.5) * 255);
        normals[idx + 3] = 255;
      }
    }
    return { width: w, height: h, pixels: normals };
  }

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.decoding = 'async';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('image load failed'));
      img.src = src;
    });
  }

  function GravityRelightEngine(stage, sourceEl) {
    this.stage = stage;
    this.sourceEl = sourceEl;
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'sim-canvas';
    this.canvas.setAttribute('aria-hidden', 'true');
    stage.insertBefore(this.canvas, sourceEl);

    const gl =
      this.canvas.getContext('webgl', {
        alpha: false,
        antialias: false,
        depth: false,
        stencil: false,
        preserveDrawingBuffer: false,
        powerPreference: 'high-performance',
      }) ||
      this.canvas.getContext('experimental-webgl', {
        alpha: false,
        antialias: false,
      });

    if (!gl) throw new Error('WebGL unavailable');

    this.gl = gl;
    this.ready = false;
    this.texW = 0;
    this.texH = 0;
    this.state = {
      x: 50,
      y: 38,
      intensity: 40,
      beam: 120,
      modifier: 'softbox',
      ambient: 70,
      temp: 55,
    };

    const vs = createShader(gl, gl.VERTEX_SHADER, VERT_SRC);
    const fs = createShader(gl, gl.FRAGMENT_SHADER, FRAG_SRC);
    this.program = createProgram(gl, vs, fs);
    gl.deleteShader(vs);
    gl.deleteShader(fs);

    this.buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW
    );

    this.albedoTex = gl.createTexture();
    this.normalTex = gl.createTexture();
    this.attribs = {
      pos: gl.getAttribLocation(this.program, 'a_pos'),
    };
    this.uniforms = {
      albedo: gl.getUniformLocation(this.program, 'u_albedo'),
      normal: gl.getUniformLocation(this.program, 'u_normal'),
      lightPos: gl.getUniformLocation(this.program, 'u_lightPos'),
      intensity: gl.getUniformLocation(this.program, 'u_intensity'),
      ambient: gl.getUniformLocation(this.program, 'u_ambient'),
      beam: gl.getUniformLocation(this.program, 'u_beam'),
      soft: gl.getUniformLocation(this.program, 'u_soft'),
      lightColor: gl.getUniformLocation(this.program, 'u_lightColor'),
      viewAspect: gl.getUniformLocation(this.program, 'u_viewAspect'),
      texel: gl.getUniformLocation(this.program, 'u_texel'),
      uvScale: gl.getUniformLocation(this.program, 'u_uvScale'),
      uvOffset: gl.getUniformLocation(this.program, 'u_uvOffset'),
    };
    this.viewAspect = 16 / 11;

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.albedoTex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.normalTex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  }

  GravityRelightEngine.prototype.tempColor = function tempColor(t) {
    const warm = [255, 197, 143];
    const neut = [255, 241, 222];
    const cool = [196, 216, 255];
    let a;
    let b;
    let f;
    if (t < 50) {
      a = warm;
      b = neut;
      f = t / 50;
    } else {
      a = neut;
      b = cool;
      f = (t - 50) / 50;
    }
    return a.map((c, i) => (c + (b[i] - c) * f) / 255);
  };

  GravityRelightEngine.prototype.uploadTexture = function uploadTexture(
    tex,
    unit,
    imgOrData,
    w,
    h
  ) {
    const gl = this.gl;
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, tex);

    if (imgOrData instanceof HTMLCanvasElement) {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, imgOrData);
      if (unit === 0) {
        this.texW = imgOrData.width;
        this.texH = imgOrData.height;
      }
      return;
    }

    if (imgOrData instanceof Image || imgOrData instanceof HTMLImageElement) {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, imgOrData);
      if (unit === 0) {
        this.texW = imgOrData.naturalWidth;
        this.texH = imgOrData.naturalHeight;
      }
      return;
    }

    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      w,
      h,
      0,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      imgOrData
    );
    if (unit === 0 && w && h) {
      this.texW = w;
      this.texH = h;
    }
  };

  GravityRelightEngine.prototype.setImage = function setImage(src) {
    const self = this;
    return loadImage(src).then(function (img) {
      const scale = Math.min(1, MAX_TEX / Math.max(img.naturalWidth, img.naturalHeight));
      const w = Math.max(2, Math.round(img.naturalWidth * scale));
      const h = Math.max(2, Math.round(img.naturalHeight * scale));
      const scratch = document.createElement('canvas');
      scratch.width = w;
      scratch.height = h;
      const ctx = scratch.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);

      self.uploadTexture(self.albedoTex, 0, scratch);
      if (!self.texW || !self.texH) {
        throw new Error('albedo texture upload failed');
      }
      const normal = buildNormalMapFromImage(img);
      self.uploadTexture(self.normalTex, 1, normal.pixels, normal.width, normal.height);
      self.ready = true;
      self.draw();
    });
  };

  GravityRelightEngine.prototype.setState = function setState(next) {
    Object.assign(this.state, next);
  };

  function coverTransform(texW, texH, viewW, viewH) {
    const texAspect = texW / texH;
    const viewAspect = viewW / viewH;
    if (texAspect > viewAspect) {
      const scaleX = viewAspect / texAspect;
      return { scale: [scaleX, 1], offset: [(1 - scaleX) * 0.5, 0] };
    }
    const scaleY = texAspect / viewAspect;
    return { scale: [1, scaleY], offset: [0, (1 - scaleY) * 0.5] };
  }

  GravityRelightEngine.prototype.resize = function resize() {
    const rect = this.stage.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, Math.round(rect.width * dpr));
    const h = Math.max(1, Math.round(rect.height * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
      this.canvas.style.width = rect.width + 'px';
      this.canvas.style.height = rect.height + 'px';
    }
    this.viewAspect = rect.width / Math.max(rect.height, 1);
  };

  GravityRelightEngine.prototype.draw = function draw() {
    if (!this.ready) return;
    const gl = this.gl;
    const s = this.state;
    this.resize();

    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.useProgram(this.program);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buf);
    gl.enableVertexAttribArray(this.attribs.pos);
    gl.vertexAttribPointer(this.attribs.pos, 2, gl.FLOAT, false, 0, 0);

    const soft = s.modifier === 'softbox' ? 1 : 0;
    const rgb = this.tempColor(s.temp);
    gl.uniform1i(this.uniforms.albedo, 0);
    gl.uniform1i(this.uniforms.normal, 1);
    gl.uniform2f(this.uniforms.lightPos, s.x / 100, s.y / 100);
    gl.uniform1f(this.uniforms.intensity, s.intensity / 100);
    gl.uniform1f(this.uniforms.ambient, (s.ambient != null ? s.ambient : 70) / 100);
    gl.uniform1f(this.uniforms.beam, s.beam);
    gl.uniform1f(this.uniforms.soft, soft);
    gl.uniform3f(this.uniforms.lightColor, rgb[0], rgb[1], rgb[2]);
    gl.uniform1f(this.uniforms.viewAspect, this.viewAspect);
    gl.uniform2f(
      this.uniforms.texel,
      this.texW > 0 ? 1 / this.texW : 1 / 2048,
      this.texH > 0 ? 1 / this.texH : 1 / 2048
    );
    const cover = coverTransform(this.texW, this.texH, this.viewAspect, 1);
    gl.uniform2f(this.uniforms.uvScale, cover.scale[0], cover.scale[1]);
    gl.uniform2f(this.uniforms.uvOffset, cover.offset[0], cover.offset[1]);

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  };

  GravityRelightEngine.isSupported = function isSupported() {
    try {
      const c = document.createElement('canvas');
      return !!(c.getContext('webgl') || c.getContext('experimental-webgl'));
    } catch {
      return false;
    }
  };

  global.GravityRelightEngine = GravityRelightEngine;
})(typeof window !== 'undefined' ? window : globalThis);
